import { alpha, type Theme } from '@mui/material/styles';

type SupportShellVariant = 'default' | 'floating';

function supportAccent(theme: Theme) {
  return theme.palette.primary.main;
}

function supportAccentLight(theme: Theme) {
  return theme.palette.primary.light;
}

/** גרדיאנט בסיס — עדין, מבוסס פלטת המערכת */
function gradientBase(theme: Theme, variant: SupportShellVariant) {
  const isDark = theme.palette.mode === 'dark';
  const accent = alpha(supportAccent(theme), isDark ? 0.14 : 0.08);
  const paper = alpha(theme.palette.background.paper, isDark ? 0.55 : 0.92);
  if (variant === 'floating') {
    return theme.palette.background.paper;
  }
  return isDark
    ? `linear-gradient(168deg, ${alpha('#16192a', 0.95)} 0%, ${alpha('#1a1528', 0.9)} 45%, ${alpha('#141d2a', 0.95)} 100%)`
    : `linear-gradient(168deg, ${alpha(supportAccentLight(theme), 0.12)} 0%, ${theme.palette.background.paper} 42%, ${alpha(supportAccent(theme), 0.04)} 100%)`;
}

/** שכבת "אורה" דקורטיבית — רק במצב מוטמע (לא בווידג'ט צף) */
function orbLayer(theme: Theme, variant: SupportShellVariant) {
  if (variant === 'floating') return 'none';
  const isDark = theme.palette.mode === 'dark';
  const accent = supportAccent(theme);
  return [
    `radial-gradient(ellipse 85% 60% at 0% 0%, ${alpha(accent, isDark ? 0.2 : 0.12)} 0%, transparent 58%)`,
    `radial-gradient(ellipse 75% 50% at 100% 15%, ${alpha(theme.palette.info.main, isDark ? 0.12 : 0.08)} 0%, transparent 50%)`,
    `radial-gradient(ellipse 70% 45% at 100% 100%, ${alpha(theme.palette.secondary.main, isDark ? 0.14 : 0.1)} 0%, transparent 52%)`,
  ].join(', ');
}

/** מעטפת מלאה לעמודת פאנל (כולל שורת קיצורים) */
export function technicalSupportAppShellProps(theme: Theme, variant: SupportShellVariant = 'default') {
  const orbs = orbLayer(theme, variant);
  return {
    position: 'relative' as const,
    overflow: 'hidden' as const,
    background: gradientBase(theme, variant),
    ...(orbs !== 'none'
      ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: orbs,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }
      : {}),
  };
}

/** כותרת משותפת — פאנל תמיכה מוטמע */
export function technicalSupportHeaderRowSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const accent = supportAccent(theme);
  return {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0.75,
    px: 1.5,
    py: 1.125,
    minWidth: 0,
    minHeight: 48,
    flexWrap: 'nowrap' as const,
    borderBottom: `1px solid ${alpha(accent, isDark ? 0.22 : 0.14)}`,
    background: isDark
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.42)} 0%, ${alpha(theme.palette.background.default, 0.28)} 100%)`
      : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.88)} 0%, ${alpha(supportAccentLight(theme), 0.14)} 100%)`,
    backdropFilter: 'blur(12px) saturate(1.1)',
    WebkitBackdropFilter: 'blur(12px) saturate(1.1)',
    boxShadow: `inset 0 -1px 0 ${alpha(theme.palette.secondary.main, isDark ? 0.08 : 0.05)}`,
  };
}

/** כותרת ממותגת — ווידג'ט צף (סגנון Intercom / HubSpot / Drift) */
export function technicalSupportFloatingBrandHeaderSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark ?? primary;
  return {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: 2,
    py: 1.75,
    minWidth: 0,
    minHeight: 76,
    flexWrap: 'nowrap' as const,
    background: isDark
      ? `linear-gradient(128deg, ${primaryDark} 0%, ${primary} 55%, ${alpha(theme.palette.secondary.main, 0.85)} 100%)`
      : `linear-gradient(128deg, ${primary} 0%, ${alpha(theme.palette.primary.light, 0.95)} 100%)`,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 4px 16px ${alpha(primary, isDark ? 0.35 : 0.22)}`,
  };
}

/** אווטאר בכותרת הווידג'ט הצף */
export function technicalSupportFloatingAvatarSx(theme: Theme) {
  return {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    bgcolor: alpha(theme.palette.common.white, 0.18),
    border: `2px solid ${alpha(theme.palette.common.white, 0.32)}`,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`,
  } as const;
}

/** כפתורי כותרת על רקע ממותג (קיצורים / סגירה) */
export function technicalSupportFloatingHeaderIconButtonSx(theme: Theme) {
  return {
    width: 36,
    height: 36,
    p: 0.5,
    borderRadius: '10px',
    color: alpha(theme.palette.primary.contrastText, 0.92),
    transition: theme.transitions.create(['background-color', 'color', 'transform'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      bgcolor: alpha(theme.palette.common.white, 0.16),
      color: theme.palette.primary.contrastText,
      transform: 'scale(1.04)',
    },
  } as const;
}

/** כפתורי קיצור בכותרת מוטמעת */
export function technicalSupportShortcutButtonSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const accent = supportAccent(theme);
  return {
    width: 32,
    height: 32,
    p: 0.5,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: alpha(accent, isDark ? 0.32 : 0.18),
    color: 'text.secondary',
    bgcolor: alpha(theme.palette.background.paper, isDark ? 0.12 : 0.72),
    transition: theme.transitions.create(['background-color', 'border-color', 'color', 'transform', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&:hover': {
      bgcolor: alpha(accent, isDark ? 0.18 : 0.1),
      borderColor: alpha(accent, isDark ? 0.45 : 0.32),
      color: 'text.primary',
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(accent, 0.14)}`,
    },
  } as const;
}

/** כפתורי הגדרות בכותרת (ניקוי, חיבור) */
export function technicalSupportHeaderActionButtonSx(theme: Theme) {
  return {
    width: 32,
    height: 32,
    p: 0.5,
    borderRadius: 1.25,
    color: 'text.secondary',
    transition: theme.transitions.create(['background-color', 'color', 'transform'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      bgcolor: alpha(supportAccent(theme), theme.palette.mode === 'dark' ? 0.14 : 0.08),
      color: 'text.primary',
    },
  } as const;
}

/** כפתור סגירה / כיווץ */
export function technicalSupportCloseButtonSx(theme: Theme) {
  return {
    ...technicalSupportHeaderActionButtonSx(theme),
    flexShrink: 0,
  } as const;
}

/** אזור הודעות — ווידג'ט צף */
export function technicalSupportFloatingMessageAreaSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    bgcolor: isDark ? alpha(theme.palette.common.black, 0.22) : '#f0f2f5',
    backgroundImage: isDark
      ? 'none'
      : `radial-gradient(circle at 20% 10%, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 45%)`,
  };
}

/** בועת הודעת AI — מוטמע */
export function technicalSupportAssistantBubbleExtra(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const accent = supportAccent(theme);
  return {
    background: isDark
      ? `linear-gradient(150deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.background.default, 0.88)} 100%)`
      : `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(supportAccentLight(theme), 0.22)} 100%)`,
    border: '1px solid',
    borderColor: alpha(accent, isDark ? 0.22 : 0.14),
    borderRadius: '14px',
    boxShadow: `0 1px 2px ${alpha(accent, isDark ? 0.1 : 0.05)}, 0 6px 18px -6px ${alpha(accent, isDark ? 0.16 : 0.1)}`,
  };
}

/** בועת הודעת AI — ווידג'ט צף (לבן נקי + צל עדין) */
export function technicalSupportFloatingAssistantBubbleSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    bgcolor: theme.palette.background.paper,
    border: '1px solid',
    borderColor: alpha(theme.palette.divider, isDark ? 0.55 : 0.9),
    borderRadius: '18px',
    boxShadow: isDark
      ? `0 2px 8px ${alpha(theme.palette.common.black, 0.28)}`
      : `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}, 0 4px 12px ${alpha(theme.palette.common.black, 0.04)}`,
    maxWidth: '88%',
  };
}

/** בועת הודעת משתמש — ווידג'ט צף */
export function technicalSupportFloatingUserBubbleSx(theme: Theme) {
  const primary = theme.palette.primary.main;
  return {
    bgcolor: primary,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.direction === 'rtl' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    maxWidth: '85%',
    boxShadow: `0 2px 10px ${alpha(primary, 0.32)}`,
  };
}

/** שכבת ה-AI (embed) — זכוכית מעל הגרדיאנט */
export function technicalSupportInlayContainerSx(theme: Theme, floating = false) {
  const isDark = theme.palette.mode === 'dark';
  const accent = supportAccent(theme);
  if (floating) {
    return {
      position: 'relative' as const,
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      borderRadius: 0,
    };
  }
  return {
    position: 'relative' as const,
    background: isDark
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.55)} 0%, ${alpha(theme.palette.background.default, 0.62)} 100%)`
      : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.55)} 0%, ${alpha(supportAccentLight(theme), 0.18)} 100%)`,
    backdropFilter: 'blur(14px) saturate(1.15)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.15)',
    borderInlineStart: `1px solid ${alpha(accent, isDark ? 0.28 : 0.16)}`,
    boxShadow: isDark
      ? `inset 0 0 0 1px ${alpha('#fff', 0.04)}, 0 0 48px -20px ${alpha(accent, 0.28)}`
      : `inset 0 0 0 1px ${alpha('#fff', 0.55)}, 0 0 40px -16px ${alpha(accent, 0.14)}`,
  };
}

/** אזור תחתון — מוטמע */
export function technicalSupportFooterSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const accent = supportAccent(theme);
  return {
    flexShrink: 0,
    position: 'sticky' as const,
    bottom: 0,
    zIndex: 4,
    background: isDark
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.75)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`
      : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.78)} 0%, ${alpha(supportAccentLight(theme), 0.2)} 100%)`,
    backdropFilter: 'blur(14px) saturate(1.15)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.15)',
    borderTop: `1px solid ${alpha(accent, isDark ? 0.2 : 0.12)}`,
    boxShadow: `0 -10px 28px -14px ${alpha(accent, 0.14)}`,
    pt: 1.375,
    px: 1.75,
    pb: 1.625,
  };
}

/** אזור תחתון — ווידג'ט צף */
export function technicalSupportFloatingFooterSx(theme: Theme) {
  return {
    flexShrink: 0,
    position: 'sticky' as const,
    bottom: 0,
    zIndex: 4,
    bgcolor: theme.palette.background.paper,
    borderTop: '1px solid',
    borderColor: alpha(theme.palette.divider, 0.9),
    boxShadow: `0 -4px 20px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.2 : 0.06)}`,
    pt: 1.5,
    px: 2,
    pb: 2,
  };
}

/** צ'יפ הצעה — מוטמע */
export function technicalSupportSuggestionChipSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const accent = supportAccent(theme);
  return {
    height: 'auto',
    minHeight: 34,
    borderRadius: 2,
    maxWidth: '100%',
    fontWeight: 500,
    fontSize: '0.8125rem',
    py: 0.4,
    color: isDark ? alpha(theme.palette.primary.light, 0.95) : theme.palette.primary.dark,
    backgroundColor: alpha(accent, isDark ? 0.12 : 0.06),
    border: `1px solid ${alpha(accent, isDark ? 0.34 : 0.2)}`,
    transition: theme.transitions.create(['transform', 'box-shadow', 'background-color', 'border-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 14px ${alpha(accent, 0.16)}`,
      backgroundColor: alpha(accent, isDark ? 0.2 : 0.1),
      borderColor: alpha(accent, isDark ? 0.42 : 0.3),
    },
  } as const;
}

/** צ'יפ הצעה — ווידג'ט צף (כפתורי quick-reply מלאים) */
export function technicalSupportFloatingSuggestionChipSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    height: 'auto',
    minHeight: 40,
    borderRadius: '12px',
    maxWidth: '100%',
    width: '100%',
    fontWeight: 500,
    fontSize: '0.875rem',
    py: 0.75,
    justifyContent: 'flex-start',
    color: 'text.primary',
    bgcolor: isDark ? alpha(theme.palette.common.white, 0.06) : theme.palette.background.paper,
    border: '1px solid',
    borderColor: alpha(theme.palette.divider, isDark ? 0.5 : 0.95),
    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, isDark ? 0.2 : 0.04)}`,
    transition: theme.transitions.create(['transform', 'box-shadow', 'border-color', 'background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      transform: 'translateY(-1px)',
      borderColor: alpha(theme.palette.primary.main, 0.4),
      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.1 : 0.04),
      boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.12)}`,
    },
  } as const;
}

/** שדה קלט pill — ווידג'ט צף */
export function technicalSupportFloatingInputFieldSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: '24px',
      bgcolor: isDark ? alpha(theme.palette.common.white, 0.06) : '#f0f2f5',
      fontSize: '0.9375rem',
      pl: 0.5,
      pr: 0.5,
      py: 0.25,
      transition: theme.transitions.create(['box-shadow', 'background-color'], {
        duration: theme.transitions.duration.shorter,
      }),
      '& fieldset': { border: 'none' },
      '&:hover': {
        bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : '#e8eaed',
      },
      '&.Mui-focused': {
        bgcolor: theme.palette.background.paper,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.28)}, 0 4px 16px ${alpha(theme.palette.common.black, isDark ? 0.25 : 0.08)}`,
      },
    },
  } as const;
}
